import React, { useState } from 'react';
import { Col, Row } from 'antd';
import { I18n } from '@aws-amplify/core';
import type { ColumnsType } from 'antd/lib/table';

import { StatusFile, enumStatusFile } from '@/utils/enum';

import { SectionTitle } from '@/components/views/BeraJob/BeraReportV2/_components/ReportResult/styles';
import { toAnalyzedTime } from '../utils';
import type { AnalyzedActivity } from '../utils';
import { FileCell } from './FileCell';
import { VideoModal } from './VideoModal';
import { ActivitiesTable, PaginationArrow } from './styles';

const TITLE_GUTTER = 16;
const PAGE_SIZE = 10;
const FILE_COLUMN_WIDTH = '18.125rem';
const NO_VALUE = '-';

interface AnalyzedActivitiesProps {
	activities: AnalyzedActivity[];
	onRetry: (activity: AnalyzedActivity) => void;
	retryingActivityId: string | null;
}

function renderTime(totalTime: number): string {
	const result = toAnalyzedTime(totalTime);

	return result;
}

function renderStatus(status: StatusFile | null): string {
	if (!status) {
		return NO_VALUE;
	}

	const result = I18n.get(enumStatusFile[status] || NO_VALUE);

	return result;
}

function renderTotal(total: number, range: [number, number]): string {
	const [first, last] = range;
	const result = `${I18n.get('Displaying')} ${first}-${last} ${I18n.get('of')} ${total}`;

	return result;
}

function renderPaginationItem(
	_page: number,
	type: 'page' | 'prev' | 'next' | 'jump-prev' | 'jump-next',
	element: React.ReactNode
): React.ReactNode {
	if (type === 'prev') {
		return <PaginationArrow $direction="previous" />;
	}

	if (type === 'next') {
		return <PaginationArrow $direction="next" />;
	}

	return element;
}

function buildColumns(
	onOpenVideo: (activity: AnalyzedActivity) => void,
	onRetryClip: (activity: AnalyzedActivity) => void,
	retryingActivityId: string | null
): ColumnsType<AnalyzedActivity> {
	const columns: ColumnsType<AnalyzedActivity> = [
		{ title: I18n.get('TLID'), dataIndex: 'task_name', key: 'task_name' },
		{
			title: I18n.get('File'),
			dataIndex: 'file_name',
			key: 'file_name',
			width: FILE_COLUMN_WIDTH,
			render: (_fileName: string, activity: AnalyzedActivity): JSX.Element => {
				const isRetrying = retryingActivityId === activity.id;

				return (
					<FileCell activity={activity} onOpen={onOpenVideo} onRetry={onRetryClip} retrying={isRetrying} />
				);
			}
		},
		{ title: I18n.get('Time'), dataIndex: 'total_time', key: 'total_time', render: renderTime },
		{ title: I18n.get('Status'), dataIndex: 'status', key: 'status', render: renderStatus }
	];

	return columns;
}

export function AnalyzedActivities({
	activities,
	onRetry,
	retryingActivityId
}: Readonly<AnalyzedActivitiesProps>): JSX.Element {
	const [openActivity, setOpenActivity] = useState<AnalyzedActivity | null>(null);

	function handleOpenVideo(activity: AnalyzedActivity): void {
		setOpenActivity(activity);
	}

	function handleCloseVideo(): void {
		setOpenActivity(null);
	}

	const columns = buildColumns(handleOpenVideo, onRetry, retryingActivityId);

	return (
		<Row gutter={[0, TITLE_GUTTER]}>
			<Col span={24}>
				<SectionTitle>{I18n.get('Analyzed activities')}</SectionTitle>
			</Col>
			<Col span={24}>
				<ActivitiesTable
					rowKey="id"
					columns={columns}
					dataSource={activities}
					pagination={{
						pageSize: PAGE_SIZE,
						showSizeChanger: false,
						hideOnSinglePage: false,
						showTotal: renderTotal,
						itemRender: renderPaginationItem
					}}
				/>
			</Col>
			{!!openActivity?.video_url && (
				<VideoModal
					title={openActivity.file_name}
					url={openActivity.video_url}
					onClose={handleCloseVideo}
				/>
			)}
		</Row>
	);
}
